/**
 * The logging correlation context — the seam that carries `sessionId` down to
 * any `useLogger()` call in the tree (see ./useLogger.ts).
 *
 * `leadId` IS part of it, unlike user identity: every conversion query joins
 * an ad click to what the reader did next on that one key, so it has to be on
 * every event rather than supplied per call.
 *
 * User identity is not. Authenticated consumers layer their auth provider's
 * opaque subject in per call with `useLogger({ userId })`; see
 * LoggerProvider.tsx for why the composition root stays above auth.
 */
import { createContext, use } from "react";

export interface LoggerContextValue {
  sessionId: string;
  /**
   * The marketing lead this browser arrived as, resolved once per landing by
   * LoggerProvider. `undefined` for a visit that carried no campaign
   * parameters and had no lead stored from an earlier one.
   */
  leadId?: string;
}

export const LoggerContext = createContext<LoggerContextValue | null>(null);

/** Read the logging context. Throws outside a `LoggerProvider`. */
export function useLoggerContext(): LoggerContextValue {
  const ctx = use(LoggerContext);
  if (!ctx) {
    throw new Error("useLoggerContext must be used within a LoggerProvider");
  }
  return ctx;
}
